// Centralized user settings handling (hydrate, read, update & persist)
import api from './api';
import { getUserInfo } from './user';

const SETTINGS_STORAGE_KEY = 'userSettings';

// Safely parse JSON; return fallback on failure
function safeParse(json, fallback) {
  try { return JSON.parse(json); } catch { return fallback; }
}

function deepParseIfString(value) {
  let current = value;
  let safety = 0;
  while (typeof current === 'string' && safety < 3) {
    const trimmed = current.trim();
    if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) break;
    const parsed = safeParse(trimmed, null);
    if (!parsed) break;
    current = parsed;
    safety++;
  }
  return current;
}

export function hydrateSettingsFromUser(user) {
  if (!user || typeof window === 'undefined') return;
  let settingsObj = {};

  // Backends may expose a consolidated JSON field `settings` (string or object)
  if (user.settings) {
    if (typeof user.settings === 'string') {
      settingsObj = deepParseIfString(user.settings) || {};
    } else if (typeof user.settings === 'object') {
      settingsObj = { ...user.settings };
    }
  }

  // Also collect individually stored preference columns (fallback style)
  if (user.dashboard_trial_columns && !settingsObj.dashboard_trial_columns) {
    settingsObj.dashboard_trial_columns = user.dashboard_trial_columns;
  }

  // Only persist if we actually have some settings data; otherwise keep existing cache
  const hasData = settingsObj && Object.keys(settingsObj).length > 0;
  if (hasData) {
    try { sessionStorage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify(settingsObj)); } catch {}
  } else {
    // If no settings from server but we already have cached, skip overwrite
    if (!sessionStorage.getItem(SETTINGS_STORAGE_KEY)) {
      try { sessionStorage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify({})); } catch {}
    }
  }

  // Apply known settings to their specific sessionStorage keys for UI hydration
  if (settingsObj.dashboard_trial_columns) {
    try {
      const cols = typeof settingsObj.dashboard_trial_columns === 'string'
        ? safeParse(settingsObj.dashboard_trial_columns, [])
        : settingsObj.dashboard_trial_columns;
      if (Array.isArray(cols) && cols.length) {
        const filtered = cols.filter(k => k !== 'id');
        sessionStorage.setItem('dashboardTrialColumns', JSON.stringify(filtered));
  try { window.dispatchEvent(new CustomEvent('dashboardColumnsHydrated', { detail: filtered })); } catch {}
      }
    } catch { /* ignore */ }
  }
  // Trials overview column preferences
  if (settingsObj.overview_trial_columns) {
    try {
      const cols = typeof settingsObj.overview_trial_columns === 'string'
        ? safeParse(settingsObj.overview_trial_columns, [])
        : settingsObj.overview_trial_columns;
      if (Array.isArray(cols) && cols.length) {
        const filtered = cols.filter(k => k !== 'id');
        sessionStorage.setItem('trialsOverviewColumns', JSON.stringify(filtered));
        try { window.dispatchEvent(new CustomEvent('trialsOverviewColumnsHydrated', { detail: filtered })); } catch {}
      }
    } catch { /* ignore */ }
  }
  // Per-page preferences (dashboard & trials overview)
  if (settingsObj.dashboard_trials_per_page) {
    try {
      sessionStorage.setItem('dashboardTrialsPerPage', String(settingsObj.dashboard_trials_per_page));
      try { window.dispatchEvent(new CustomEvent('dashboardPerPageHydrated', { detail: settingsObj.dashboard_trials_per_page })); } catch {}
    } catch { /* ignore */ }
  }
  if (settingsObj.trials_per_page) {
    try {
      sessionStorage.setItem('trialsPerPage', String(settingsObj.trials_per_page));
      try { window.dispatchEvent(new CustomEvent('trialsPerPageHydrated', { detail: settingsObj.trials_per_page })); } catch {}
    } catch { /* ignore */ }
  }
  // For pages that read their preferences with getUserSettings() and may have mounted first
  try { window.dispatchEvent(new CustomEvent('userSettingsHydrated', { detail: settingsObj })); } catch { /* no window events */ }
  // Output settings JSON for debugging
  try { console.debug('[settings] hydrateSettingsFromUser ->', settingsObj); } catch {}
}

export function getUserSettings() {
  if (typeof window === 'undefined') return {};
  const raw = sessionStorage.getItem(SETTINGS_STORAGE_KEY);
  if (!raw) return {};
  return safeParse(raw, {});
}

export async function updateUserSettings(patch) {
  if (!patch || typeof patch !== 'object') return false;

  // Normalize incoming patch (allow caller to pass stringified columns or array)
  let normalizedPatch = { ...patch };
  if (normalizedPatch.dashboard_trial_columns) {
    if (typeof normalizedPatch.dashboard_trial_columns === 'string') {
      // try parse; if parse fails keep string
      const parsed = safeParse(normalizedPatch.dashboard_trial_columns, null);
      if (Array.isArray(parsed)) normalizedPatch.dashboard_trial_columns = parsed;
    }
  }

  // The whole settings object is sent and replaces the saved one: before the user's settings are loaded in this tab
  // (a new tab, a page saving on mount), load them first, or every other saved setting is lost
  if (typeof window !== 'undefined' && sessionStorage.getItem(SETTINGS_STORAGE_KEY) === null) {
    await getUserInfo(true);
  }

  // Merge with current cached settings object
  const current = getUserSettings();

  // Nothing changed: skip the request. Pages persist their preferences from effects that also run
  // on mount, which otherwise POSTed unchanged values on every page load.
  const unchanged = Object.keys(normalizedPatch).every(
    (key) => key in current && JSON.stringify(current[key]) === JSON.stringify(normalizedPatch[key])
  );
  if (unchanged) return true;

  const mergedSettings = { ...current, ...normalizedPatch };
  // Update the cache before the request, not after: pages save several preferences at once, and
  // each call must build on the others or the last response drops their keys (locally, and on the
  // server too since the whole settings object is sent). Rolled back below if the request fails.
  try { sessionStorage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify(mergedSettings)); } catch { /* storage unavailable */ }

  // Prepare outbound payload
  const outbound = { ...normalizedPatch };
  // Provide individual legacy fields stringified for backward compatibility (if backend used it previously)
  if (Array.isArray(mergedSettings.dashboard_trial_columns)) {
    outbound.dashboard_trial_columns = JSON.stringify(mergedSettings.dashboard_trial_columns);
  }
  // Include per-page preferences if present
  if (mergedSettings.dashboard_trials_per_page !== undefined) {
    outbound.dashboard_trials_per_page = mergedSettings.dashboard_trials_per_page;
  }
  if (mergedSettings.trials_per_page !== undefined) {
    outbound.trials_per_page = mergedSettings.trials_per_page;
  }
  // Send both string and object forms for maximum backend compatibility
  outbound.settings = JSON.stringify(mergedSettings); // if backend expects string
  outbound.settings_object = mergedSettings; // if backend handles object

  // Debug output (can be silenced in production by removing or gating with env var)
  try {
    if (import.meta?.env?.VITE_DEBUG_MODE === 'true') {
      // eslint-disable-next-line no-console
      console.debug('[settings] updateUserSettings patch:', patch);
      // eslint-disable-next-line no-console
      console.debug('[settings] updateUserSettings outbound payload:', outbound);
    }
  } catch {}

  try {
  await api.post('/me/update', outbound);
    if (Array.isArray(mergedSettings.dashboard_trial_columns)) {
      sessionStorage.setItem('dashboardTrialColumns', JSON.stringify(mergedSettings.dashboard_trial_columns.filter(k => k !== 'id')));
    }
    if (mergedSettings.dashboard_trials_per_page !== undefined) {
      sessionStorage.setItem('dashboardTrialsPerPage', String(mergedSettings.dashboard_trials_per_page));
    }
    if (mergedSettings.trials_per_page !== undefined) {
      sessionStorage.setItem('trialsPerPage', String(mergedSettings.trials_per_page));
    }
    window.dispatchEvent(new CustomEvent('userSettingsUpdated', { detail: Object.keys(patch) }));
  // Force refresh user info to validate backend persisted JSON
    // Attempt a delayed refresh only if debug mode enabled to verify persistence
    try {
      if (import.meta?.env?.VITE_DEBUG_MODE === 'true') {
        setTimeout(() => { getUserInfo(true); }, 1200);
      }
    } catch {}
    try { console.debug('[settings] updateUserSettings success merged ->', mergedSettings); } catch {}
    return true;
  } catch (e) {
    // Roll back only this call's keys, keeping whatever other calls stored meanwhile
    try {
      const latest = getUserSettings();
      for (const key of Object.keys(normalizedPatch)) {
        if (key in current) latest[key] = current[key];
        else delete latest[key];
      }
      sessionStorage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify(latest));
    } catch { /* storage unavailable */ }
    console.warn('Failed updating user settings', e?.response?.status || e?.message);
    return false;
  }
}

export function logCurrentSettings() {
  try {
    const raw = sessionStorage.getItem(SETTINGS_STORAGE_KEY);
    console.debug('[settings] current cached settings raw ->', raw);
    if (raw) console.debug('[settings] parsed ->', safeParse(raw, {}));
  } catch {}
}
